
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useStore } from "@/store/useStore";
import { useAuthContext } from "@/contexts/auth";
import { Form } from "@/components/ui/form";
import { formSchema, FormValues } from "./types";
import { MetalSelect } from "./MetalSelect";
import { ConditionSelect } from "./ConditionSelect";
import { PriceTargetInput } from "./PriceTargetInput";
import { FormActions } from "./FormActions";

export function PriceAlertForm() {
  const { createPriceAlert, currentGoldPrice, currentSilverPrice } = useStore();
  const { user, isAuthenticated } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Debug logging per lo stato di autenticazione
  useEffect(() => {
    console.log("[PriceAlertForm] Auth state check:", { 
      contextAuth: isAuthenticated,
      userExists: !!user,
      userId: user?.id,
      combinedAuth: isAuthenticated && !!user
    });
  }, [user, isAuthenticated]);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      metal: "gold",
      targetPrice: currentGoldPrice?.price || 0,
      condition: "above",
    },
  });
  
  // Update default values when prices are loaded
  useEffect(() => {
    if (currentGoldPrice && form.getValues("metal") === "gold") {
      form.setValue("targetPrice", currentGoldPrice.price);
    } else if (currentSilverPrice && form.getValues("metal") === "silver") {
      form.setValue("targetPrice", currentSilverPrice.price);
    }
  }, [currentGoldPrice, currentSilverPrice, form]);
  
  const handleMetalChange = (value: "gold" | "silver") => {
    const currentPrice = value === "gold" 
      ? currentGoldPrice?.price 
      : currentSilverPrice?.price;
    
    if (currentPrice) {
      form.setValue("targetPrice", currentPrice);
    }
  };
  
  const onSubmit = async (values: FormValues) => {
    // Verifica autenticazione semplificata
    console.log("[PriceAlertForm] Submit activated with auth state:", {
      contextAuth: isAuthenticated,
      userExists: !!user,
      userId: user?.id,
      combinedAuth: isAuthenticated && !!user
    });
    
    if (!isAuthenticated || !user) {
      console.warn("[PriceAlertForm] Auth verification failed at submit");
      
      toast({
        title: "Autenticazione richiesta",
        description: "Effettua l'accesso per creare avvisi di prezzo",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("[PriceAlertForm] Creating alert with values:", values);
      console.log("[PriceAlertForm] User authentication confirmed:", { userId: user.id });
      
      // Creazione avviso senza blocchi OneSignal
      await createPriceAlert({
        metal: values.metal,
        targetPrice: values.targetPrice,
        condition: values.condition,
      });
      
      toast({
        title: "Avviso di prezzo creato",
        description: `Riceverai una notifica quando il prezzo dell'${values.metal === 'gold' ? 'oro' : 'argento'} andrà ${values.condition === 'above' ? 'sopra' : 'sotto'} €${values.targetPrice}`,
      });
      
      form.reset({
        metal: values.metal,
        targetPrice: values.metal === "gold" ? currentGoldPrice?.price || 0 : currentSilverPrice?.price || 0,
        condition: "above",
      });
    } catch (error) {
      console.error("[PriceAlertForm] Error creating alert:", error);
      toast({
        title: "Impossibile creare l'avviso",
        description: "Si è verificato un errore durante la creazione dell'avviso di prezzo",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Crea Avviso di Prezzo</CardTitle>
        <CardDescription>
          Ricevi notifiche quando i prezzi dei metalli raggiungono il tuo obiettivo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <MetalSelect 
              control={form.control} 
              onMetalChange={handleMetalChange}
              currentGoldPrice={currentGoldPrice?.price}
              currentSilverPrice={currentSilverPrice?.price}
            />
            
            <ConditionSelect control={form.control} />
            
            <PriceTargetInput 
              control={form.control} 
              watch={form.watch}
              currentGoldPrice={currentGoldPrice?.price}
              currentSilverPrice={currentSilverPrice?.price}
            />
            
            <FormActions 
              isAuthenticated={isAuthenticated && !!user} 
              isLoading={isSubmitting}
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
